import type { SenderGroupOutput } from "@/api/custom_types";
import { useTranslation } from "@/hooks/useTranslation";
import { useDeleteSenderGroup, useUpdateSenderGroup } from "@/services";
import {
	ActionItem,
	ActionMenu,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogScrollableArea,
	DialogTitle,
} from "@st-marketplace/ui";
import React, { useState } from "react";
import { SenderGroupForm } from "./SenderGroupForm";
import type { SenderGroupFormData } from "./senderGroupForm.schema";

interface SenderGroupsSingleActionProps {
	selectedGroup: SenderGroupOutput;
}

const SenderGroupsSingleAction: React.FC<SenderGroupsSingleActionProps> = ({
	selectedGroup,
}) => {
	const { t } = useTranslation();
	const updateGroupMutation = useUpdateSenderGroup();
	const deleteGroupMutation = useDeleteSenderGroup();

	const [menuActionOpen, setMenuActionOpen] = useState<boolean>(false);
	const [editDialog, setEditDialog] = useState<boolean>(false);
	const [deleteDialog, setDeleteDialog] = useState<boolean>(false);

	const initialValues = React.useMemo(
		() => ({
			name: selectedGroup.name,
			timezone: selectedGroup.timezone ?? "",
			to_keep: selectedGroup.to_keep,
			use_global_setting: selectedGroup.to_keep === null,
			use_global_priority: selectedGroup.priority_usage === null,
			priority_usage: selectedGroup.priority_usage,
		}),
		[selectedGroup],
	);

	const handleEditSubmit = async (data: SenderGroupFormData) => {
		await updateGroupMutation.mutateAsync({
			path: { group_id: selectedGroup.id },
			body: {
				name: data.name,
				to_keep: data.use_global_setting ? null : data.to_keep ?? null,
				priority_usage: data.use_global_priority
					? null
					: data.priority_usage ?? null,
			},
		});
		setEditDialog(false);
		setMenuActionOpen(false);
	};

	const handleDeleteConfirm = () => {
		deleteGroupMutation.mutate(
			{ path: { group_id: selectedGroup.id } },
			{
				onSuccess: () => {
					setDeleteDialog(false);
					setMenuActionOpen(false);
				},
			},
		);
	};

	return (
		<>
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation-only container; inner menu remains keyboard-accessible */}
			<div onClick={(event) => event.stopPropagation()}>
				<ActionMenu open={menuActionOpen} onOpenChange={setMenuActionOpen}>
					<ActionItem
						leadingIconName="edit-line"
						onClick={() => {
							setMenuActionOpen(false);
							setEditDialog(true);
						}}
					>
						{t("action.edit")}
					</ActionItem>
					<ActionItem
						leadingIconName="delete-bin-line"
						loading={deleteGroupMutation.isPending}
						onClick={() => {
							setMenuActionOpen(false);
							setDeleteDialog(true);
						}}
					>
						{t("action.delete")}
					</ActionItem>
				</ActionMenu>
			</div>

			<Dialog open={editDialog} onOpenChange={setEditDialog}>
				<DialogContent className="w-full max-w-120">
					<DialogHeader>
						<DialogTitle>{t("senderGroup.formEditTitle")}</DialogTitle>
					</DialogHeader>
					<DialogScrollableArea>
						<SenderGroupForm
							submitLabel={t("senderGroup.formSubmit")}
							initialValues={initialValues}
							loading={updateGroupMutation.isPending}
							onSubmit={handleEditSubmit}
						/>
					</DialogScrollableArea>
				</DialogContent>
			</Dialog>

			<Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t("action.areYouSure")}</DialogTitle>
						<DialogDescription>
							{t("senderGroup.action.deleteMessage", {
								name: selectedGroup.name,
							})}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter
						buttons={{
							secondary: {
								label: t("common.button.no"),
								props: {
									type: "button",
									onClick: () => setDeleteDialog(false),
								},
							},
							primary: {
								label: t("action.button.delete"),
								props: {
									type: "button",
									loading: deleteGroupMutation.isPending,
									onClick: handleDeleteConfirm,
								},
							},
						}}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default SenderGroupsSingleAction;
